import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { axiosInstance } from '../../helpers'

export const PROPERTY_LEASING_SEASON_CONFIGS_KEY =
    'property-leasing-season-configs'
export const PROPERTY_LEASING_SEASON_CONFIG_KEY =
    'property-leasing-season-config'

export const propertyLeasingSeasonConfigsKey = (
    apartmentId: number | null | undefined,
) => [PROPERTY_LEASING_SEASON_CONFIGS_KEY, apartmentId] as const

export const propertyLeasingSeasonConfigKey = (
    apartmentId: number | null | undefined,
    configId: number | null | undefined,
) => [PROPERTY_LEASING_SEASON_CONFIG_KEY, apartmentId, configId] as const

export type ApiCreatedFrom = 'local' | 'ezos' | 'org' | 'scratch'

export type ApiSlotRef = {
    id?: string | number | null
    config_id?: number | null
    name?: string | null
    source?: string | null
    org_mandated?: boolean
    used_by_other_configs?: number
    description?: string | null
    adopted_from_name?: string | null
    adopted_from_source?: string | null
    modified_at?: string | null
} | null

export type ApiLeasingSeasonConfigDefinition = {
    slots: {
        applicant_data: ApiSlotRef
        guest_card: ApiSlotRef
        application_fee: ApiSlotRef
        conditional_approvals: ApiSlotRef
        signable_docs: ApiSlotRef
        property_data: ApiSlotRef
        leads_pipeline: ApiSlotRef
    }
    edit_scope_by_slot: Record<string, 'this_config_only' | 'shared_artifact'>
    guest_card_members: Record<string, boolean>
    conditional_policy: Record<string, unknown>
    signable_role_bindings?: Record<string, string | null>
    lease_packet?: Array<Record<string, unknown>>
    guarantor_packet?: Array<Record<string, unknown>>
    workbook_sections: Record<string, unknown>
}

export type SetupPackDetail = {
    id: number
    name: string
    description: string
    apartment_id: number
    definition: Record<string, unknown>
    /** False when pinned by a leasing season config linked to an active season. */
    is_editable?: boolean
    amount?: string
    currency?: string
    copied_from_id?: number | null
    created_date?: string
    modified_date?: string
}

export type BoundSeason = {
    id: number
    name: string
    status?: 'active' | 'upcoming' | 'past'
}

export type PropertyLeasingSeasonConfigListItem = {
    id: number
    name: string
    description: string
    apartment_id: number
    apartment_name?: string
    created_from: ApiCreatedFrom
    org_mandated: boolean
    is_editable: boolean
    /** Required Setup + Compose slots are pinned. */
    is_ready?: boolean
    readiness_label?: string
    /** Present when linked to a currently active season (start ≤ today ≤ end). */
    active_bound_season?: BoundSeason | null
    /** Season using this config, if any (1:1). */
    bound_season?: BoundSeason | null
    application_workbook_id?: number | null
    application_workbook_name?: string | null
    pipeline_config_id?: number | null
    pipeline_config_name?: string | null
    guest_card_pack_id?: number | null
    signable_doc_set_id?: number | null
    fee_policy_id?: number | null
    conditional_approvals_id?: number | null
    property_data_pack_id?: number | null
    copied_from_id?: number | null
    created_date?: string
    modified_date?: string
}

export type PropertyLeasingSeasonConfigDetail = PropertyLeasingSeasonConfigListItem & {
    definition: ApiLeasingSeasonConfigDefinition
    guest_card_pack?: SetupPackDetail | null
    signable_doc_set?: SetupPackDetail | null
    fee_policy?: SetupPackDetail | null
    conditional_approvals?: SetupPackDetail | null
    property_data_pack?: SetupPackDetail | null
}

export function usePropertyLeasingSeasonConfigs(
    apartmentId: number | null | undefined,
) {
    return useQuery({
        queryKey: propertyLeasingSeasonConfigsKey(apartmentId),
        queryFn: async () => {
            const { data } = await axiosInstance.get<
                PropertyLeasingSeasonConfigListItem[]
            >(`/company/apartment/${apartmentId}/leasing-season-configs/`)
            return data
        },
        enabled: apartmentId != null,
        refetchOnWindowFocus: false,
    })
}

export function usePropertyLeasingSeasonConfig(
    apartmentId: number | null | undefined,
    configId: number | null | undefined,
) {
    return useQuery({
        queryKey: propertyLeasingSeasonConfigKey(apartmentId, configId),
        queryFn: async () => {
            const {
                data,
            } = await axiosInstance.get<PropertyLeasingSeasonConfigDetail>(
                `/company/apartment/${apartmentId}/leasing-season-configs/${configId}/`,
            )
            return data
        },
        enabled: apartmentId != null && configId != null,
        refetchOnWindowFocus: false,
    })
}

export function useCreatePropertyLeasingSeasonConfig(
    apartmentId: number | null | undefined,
) {
    const queryClient = useQueryClient()

    return useMutation({
        mutationFn: async (payload: {
            name: string
            description?: string
            created_from?: ApiCreatedFrom
            definition?: ApiLeasingSeasonConfigDefinition
            org_mandated?: boolean
        }) => {
            const {
                data,
            } = await axiosInstance.post<PropertyLeasingSeasonConfigDetail>(
                `/company/apartment/${apartmentId}/leasing-season-configs/`,
                payload,
            )
            return data
        },
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: propertyLeasingSeasonConfigsKey(apartmentId),
            })
        },
    })
}

export function useSavePropertyLeasingSeasonConfig(
    apartmentId: number | null | undefined,
    configId: number | null | undefined,
) {
    const queryClient = useQueryClient()

    return useMutation({
        mutationFn: async (payload: {
            name?: string
            description?: string
            definition?: ApiLeasingSeasonConfigDefinition
        }) => {
            const {
                data,
            } = await axiosInstance.patch<PropertyLeasingSeasonConfigDetail>(
                `/company/apartment/${apartmentId}/leasing-season-configs/${configId}/`,
                payload,
            )
            return data
        },
        onSuccess: (data) => {
            queryClient.setQueryData(
                propertyLeasingSeasonConfigKey(apartmentId, configId),
                data,
            )
            queryClient.invalidateQueries({
                queryKey: propertyLeasingSeasonConfigsKey(apartmentId),
            })
        },
    })
}

export function useCopyPropertyLeasingSeasonConfig(
    apartmentId: number | null | undefined,
) {
    const queryClient = useQueryClient()

    return useMutation({
        mutationFn: async (payload: {
            sourceConfigId: number
            name?: string
        }) => {
            const {
                data,
            } = await axiosInstance.post<PropertyLeasingSeasonConfigDetail>(
                `/company/apartment/${apartmentId}/leasing-season-configs/${payload.sourceConfigId}/copy/`,
                payload.name ? { name: payload.name } : {},
            )
            return data
        },
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: propertyLeasingSeasonConfigsKey(apartmentId),
            })
        },
    })
}
